/**
 * Data transformation utilities for converting backend API responses
 * to frontend-friendly formats
 */

import { Project, ProjectCreate, ProjectUpdate } from '../services/projects'
import { Task, TaskCreate, TaskUpdate } from '../services/tasks'
import type { ApiErrorLike } from './apiError'

/**
 * Transform backend project response to frontend format
 */
export function transformProject(backendProject: any): Project {
  return {
    id: backendProject.id,
    name: backendProject.name,
    description: backendProject.description || null,
    status: backendProject.status,
    priority: backendProject.priority,
    start_date: backendProject.start_date || null,
    end_date: backendProject.end_date || null,
    project_manager_id: backendProject.project_manager_id || null,
    budget: backendProject.budget ? String(backendProject.budget) : null,
    actual_cost: backendProject.actual_cost ? String(backendProject.actual_cost) : null,
    currency: backendProject.currency || null,
    gl_code: backendProject.gl_code || null,
    cost_center: backendProject.cost_center || null,
    progress: backendProject.progress || 0,
    organization_id: backendProject.organization_id || null,
    portfolio_id: backendProject.portfolio_id || null,
    program_id: backendProject.program_id || null,
    // Part 21 — scope/objective scaffolding.
    objective: backendProject.objective ?? null,
    key_objectives: backendProject.key_objectives ?? null,
    success_criteria: backendProject.success_criteria ?? null,
    created_at: backendProject.created_at,
    updated_at: backendProject.updated_at,
    metadata: backendProject.metadata || backendProject.extra_metadata || undefined,
  }
}

/**
 * Transform frontend project create/update to backend format
 */
export function transformProjectForBackend(
  frontendProject: ProjectCreate | ProjectUpdate
): any {
  const transformed: any = {
    ...frontendProject,
  }

  // Convert budget string to number if present
  if ('budget' in transformed && transformed.budget) {
    transformed.budget = parseFloat(transformed.budget)
  }

  // Convert actual_cost string to number if present
  if ('actual_cost' in transformed && transformed.actual_cost) {
    transformed.actual_cost = parseFloat(transformed.actual_cost)
  }

  // Handle metadata field mapping (extra_metadata in backend)
  if ('metadata' in transformed && transformed.metadata) {
    transformed.extra_metadata = transformed.metadata
    delete transformed.metadata
  }

  return transformed
}

/**
 * Transform backend task response to frontend format
 */
export function transformTask(backendTask: any): Task {
  return {
    id: backendTask.id,
    title: backendTask.title,
    description: backendTask.description || null,
    status: backendTask.status,
    priority: backendTask.priority,
    project_id: backendTask.project_id,
    assignee_id: backendTask.assignee_id || null,
    reporter_id: backendTask.reporter_id || null,
    start_date: backendTask.start_date || null,
    due_date: backendTask.due_date || null,
    estimated_hours: backendTask.estimated_hours || null,
    actual_hours: backendTask.actual_hours || null,
    created_at: backendTask.created_at,
    updated_at: backendTask.updated_at,
    metadata: backendTask.metadata || backendTask.extra_metadata || undefined,
  }
}

/**
 * Transform frontend task create/update to backend format
 */
export function transformTaskForBackend(frontendTask: TaskCreate | TaskUpdate): any {
  const transformed: any = {
    ...frontendTask,
  }

  // Handle metadata field mapping (extra_metadata in backend)
  if ('metadata' in transformed && transformed.metadata) {
    transformed.extra_metadata = transformed.metadata
    delete transformed.metadata
  }

  return transformed
}

/**
 * Format date string for display
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return 'Not set'
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return 'Invalid date'
  }
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: string | number | null | undefined): string {
  if (amount === null || amount === undefined) return '$0.00'
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(numAmount)) return '$0.00'
  return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * Calculate budget variance percentage = (actual - budget) / budget * 100.
 *
 * A positive result means over budget, negative means under budget.
 *
 * Edge cases (KQA-9):
 *  - No budget set (budget <= 0): variance is undefined → return `null`
 *    (callers render "—"). A percentage of an unknown base is meaningless.
 *  - Nothing spent yet (actualCost <= 0): a brand-new project hasn't incurred
 *    cost, so it is NOT "100% under budget". Return `null` so the UI shows
 *    "—" rather than a misleading "-100.0%".
 *
 * Returns `null` when no meaningful variance can be computed.
 */
export function calculateBudgetVariance(
  budget: string | number | null,
  actualCost: string | number | null,
): number | null {
  const budgetNum = typeof budget === 'string' ? parseFloat(budget) : budget || 0
  const actualNum = typeof actualCost === 'string' ? parseFloat(actualCost) : actualCost || 0
  if (!Number.isFinite(budgetNum) || budgetNum <= 0) return null
  if (!Number.isFinite(actualNum) || actualNum <= 0) return null
  return ((actualNum - budgetNum) / budgetNum) * 100
}

/**
 * Format a budget variance for display, returning "—" when there is no
 * meaningful variance (no budget set, or nothing spent yet).
 */
export function formatBudgetVariance(
  budget: string | number | null,
  actualCost: string | number | null,
): string {
  const v = calculateBudgetVariance(budget, actualCost)
  if (v === null) return '—'
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`
}

/**
 * Get status color class
 */
export function getStatusColor(status: string): string {
  const statusMap: Record<string, string> = {
    active: 'bg-green-100 text-green-700 border-green-300',
    planning: 'bg-blue-100 text-blue-700 border-blue-300',
    on_hold: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    completed: 'bg-gray-100 text-gray-700 border-gray-300',
    cancelled: 'bg-red-100 text-red-700 border-red-300',
    todo: 'bg-gray-100 text-gray-700 border-gray-300',
    in_progress: 'bg-blue-100 text-blue-700 border-blue-300',
    in_review: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    done: 'bg-green-100 text-green-700 border-green-300',
    blocked: 'bg-red-100 text-red-700 border-red-300',
  }
  return statusMap[status] || 'bg-gray-100 text-gray-700 border-gray-300'
}

/**
 * Get priority color class
 */
export function getPriorityColor(priority: string): string {
  const priorityMap: Record<string, string> = {
    urgent: 'bg-red-100 text-red-700 border-red-300',
    high: 'bg-orange-100 text-orange-700 border-orange-300',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    low: 'bg-gray-100 text-gray-700 border-gray-300',
  }
  return priorityMap[priority] || 'bg-gray-100 text-gray-700 border-gray-300'
}

/**
 * Transform project stats response
 */
export function transformProjectStats(backendStats: any): {
  project_id: number
  total_tasks: number
  tasks_by_status: Record<string, number>
  progress: number
  budget: string | null
  actual_cost: string | null
} {
  return {
    project_id: backendStats.project_id,
    total_tasks: backendStats.total_tasks || 0,
    tasks_by_status: backendStats.tasks_by_status || {},
    progress: backendStats.progress || 0,
    budget: backendStats.budget ? String(backendStats.budget) : null,
    actual_cost: backendStats.actual_cost ? String(backendStats.actual_cost) : null,
  }
}

/**
 * Extract error message from API error response
 * Handles FastAPI validation errors (array format) and regular error messages
 */
export function getErrorMessage(errorRaw: unknown): string {
  const error = errorRaw as ApiErrorLike | null | undefined
  if (!error) {
    return 'An unexpected error occurred'
  }

  // Handle axios error response
  if (error.response?.data) {
    const errorData = error.response.data

    // FastAPI validation errors come as an array of objects
    if (Array.isArray(errorData.detail)) {
      const validationErrors = errorData.detail
        .map((err: { loc?: Array<string | number>; msg?: string }) => {
          // Extract field name from location array (skip 'body' prefix)
          const field = err.loc?.slice(1).join('.') || 'field'
          return `${field}: ${err.msg}`
        })
        .join(', ')
      return `Validation error: ${validationErrors}`
    }

    // Single error message (string)
    if (typeof errorData.detail === 'string') {
      return errorData.detail
    }

    // Error object with message property
    if (errorData.detail && typeof errorData.detail === 'object' && 'msg' in errorData.detail) {
      return errorData.detail.msg
    }
  }

  // Fallback to error message or default
  return error.message || 'An unexpected error occurred'
}

